// ============================================================
// Bolt — resolution engine. Pure, deterministic geometry shared
// by the server (authoritative) and unit-tested in Node. Each
// card performs an EXACT geometric clip of the possible zone Z;
// the amount removed is whatever the geometry yields — there is
// NO split-fraction assumption (spec §3, §4.4).
//
// Invariants (§4.4.1):
//  • Z only ever shrinks (monotonic): area(Z') ≤ area(Z).
//  • Z stays within B — guaranteed because Z starts as B and we
//    only intersect/difference, never add.
//  • Z may be a Polygon OR a MultiPolygon.
//  • A clip that would empty Z is a NO-OP (Z is never empty).
// ============================================================
import { area, bearing as turfBearing, booleanPointInPolygon, distance, difference, featureCollection, intersect, point, simplify } from '@turf/turf';
import type { Feature, Polygon, MultiPolygon } from 'geojson';
import {
  MAX_ZONE_VERTICES,
  SIMPLIFY_TOLERANCE_DEG,
  THERMO_TIE_EPSILON_M,
} from './constants';
import type { Boundary, LngLat, Zone } from './protocol';
// v11 §2G (BOLT-027): the cut-region builders live in cutGeometry.ts, shared
// verbatim with the client previews + codex demos — preview ≡ resolution by
// construction. Re-exported here so existing engine imports keep working.
import {
  bisectorHalfPlane,
  bordersRegions,
  cornerOf,
  directionWedge,
  quadrantRegion,
  radarDisk,
  sliceHalfPlane,
  type Cardinal,
  type Corner,
} from './cutGeometry';

// buildHalfPlane = the perpendicular-bisector builder behind the frozen
// `geo.halfPlaneBisector` sandbox API (NOT the Slice card's along-line cut).
export { directionWedge as buildWedge, bisectorHalfPlane as buildHalfPlane, cornerOf, type Corner } from './cutGeometry';

type Poly = Feature<Polygon | MultiPolygon>;

const pt = (p: LngLat) => point([p.lng, p.lat]);
const distM = (a: LngLat, b: LngLat) => distance(pt(a), pt(b), { units: 'meters' });

// ---------------- direction quadrants (§4.4.1) ----------------
/** Bearing (0°=N, clockwise) → quadrant. Intervals are half-open, so a bearing
 *  exactly on a boundary falls into the higher (clockwise-next) quadrant. */
export function bearingToQuadrant(bearingDeg: number): Cardinal {
  const b = ((bearingDeg % 360) + 360) % 360;
  if (b >= 45 && b < 135) return 'E';
  if (b >= 135 && b < 225) return 'S';
  if (b >= 225 && b < 315) return 'W';
  return 'N'; // [315,360) ∪ [0,45)
}

export function directionOf(S: LngLat, H: LngLat): Cardinal {
  return bearingToQuadrant(turfBearing(pt(S), pt(H)));
}

// ---------------- clip primitives ----------------
/** Intersect Z with a clip polygon; null/empty result ⇒ no-op (keep Z). */
function clipKeep(Z: Poly, clip: Feature<Polygon>): Poly {
  const r = intersect(featureCollection([Z as Feature<Polygon | MultiPolygon>, clip]));
  return guard(Z, r);
}
/** Remove a clip polygon from Z; null/empty result ⇒ no-op (keep Z). */
function clipRemove(Z: Poly, clip: Feature<Polygon>): Poly {
  const r = difference(featureCollection([Z as Feature<Polygon | MultiPolygon>, clip]));
  return guard(Z, r);
}

/** Enforce the invariants: never empty, never grows. */
function guard(prev: Poly, next: Poly | null): Poly {
  if (!next || !next.geometry) return prev;
  const a = area(next);
  if (a <= 0) return prev; // empty ⇒ no-op
  if (a > area(prev) + 1e-6) return prev; // never grows (numerical safety)
  return next;
}

/** Douglas–Peucker simplify + hard vertex cap, preserving valid GeoJSON. */
export function simplifyZone(Z: Poly): Poly {
  let tol = SIMPLIFY_TOLERANCE_DEG;
  let out: Poly = Z;
  for (let i = 0; i < 6; i++) {
    try {
      out = simplify(Z, { tolerance: tol, highQuality: false, mutate: false }) as Poly;
    } catch {
      return Z;
    }
    if (countVertices(out) <= MAX_ZONE_VERTICES) return out;
    tol *= 2;
  }
  return out;
}

export function countVertices(Z: Poly): number {
  const g = Z.geometry;
  const rings = g.type === 'Polygon' ? g.coordinates : g.coordinates.flat();
  return rings.reduce((n, r) => n + r.length, 0);
}

/** Apply one keep/remove clip of Z by an arbitrary region, enforcing the
 *  invariants (never grows, never empties) + simplifying. The single primitive
 *  a scripted card's returned op flows through (v8 sandbox, BOLT-009), so a
 *  community card is held to the exact same monotonic-shrink guarantee as the
 *  built-ins. */
export function clipZone(Z: Poly, region: Poly, op: 'keep' | 'remove'): Poly {
  const clipped =
    op === 'keep'
      ? intersect(featureCollection([Z, region]))
      : difference(featureCollection([Z, region]));
  return simplifyZone(guard(Z, (clipped as Poly | null) ?? null));
}

// ---------------- card resolutions ----------------
export interface RadarResult {
  zone: Poly;
  inside: boolean;
}
/** Radar: keep the inside of disk(S,r) if dist(H,S) ≤ r, else keep the outside.
 *  Branch chosen by the true distance; the disk is an exact clip of Z (§4.4). */
export function resolveRadar(S: LngLat, H: LngLat, Z: Poly, radiusM: number): RadarResult {
  const inside = distM(S, H) <= radiusM;
  const disk = radarDisk(S, radiusM); // the SAME disk the preview shows (§2G)
  const zone = simplifyZone(inside ? clipKeep(Z, disk) : clipRemove(Z, disk));
  return { zone, inside };
}

export interface DirectionResult {
  zone: Poly;
  dir: Cardinal;
}
/** Direction: classify bearing S→H into one of four 90° quadrants and clip Z to
 *  that wedge (§4.4.1). */
export function resolveDirection(S: LngLat, H: LngLat, Z: Poly): DirectionResult {
  const dir = directionOf(S, H);
  const wedge = directionWedge(S, dir); // the SAME wedge the preview/demo shows (§2G/§2K)
  return { zone: simplifyZone(clipKeep(Z, wedge)), dir };
}

// ---------------- v11 §2K: Quadrants (axis-aligned cross through S) ----------------
export interface QuadrantsResult {
  zone: Poly;
  corner: Corner;
}
/** Quadrants: classify H into one of the four axis-aligned quadrants of the
 *  cross through S and clip Z to that quadrant. Same never-empty/shrink-only
 *  guarantees as Direction; same 2-bit information yield. */
export function resolveQuadrants(S: LngLat, H: LngLat, Z: Poly): QuadrantsResult {
  const corner = cornerOf(S, H);
  const region = quadrantRegion(S, corner);
  return { zone: simplifyZone(clipKeep(Z, region)), corner };
}

// ---------------- v11 §2L: Borders (outer band vs inner core) ----------------
export interface BordersResult {
  zone: Poly;
  side: 'band' | 'core';
}
/** Borders: binary region-match against the boundary's outer-25% band vs its
 *  75% core (bordersRegions — bisection-solved inset). Hider in the band ⇒
 *  the core is eliminated (the jackpot); hider in the core ⇒ the band goes
 *  (the consolation). The cut then intersects the live zone like every card. */
export function resolveBorders(H: LngLat, boundary: Boundary, Z: Poly): BordersResult {
  const regions = bordersRegions(boundary);
  if (!regions) return { zone: Z, side: 'core' }; // degenerate boundary — no-op
  const inCore = booleanPointInPolygon([H.lng, H.lat], regions.core);
  const keep = inCore ? regions.core : regions.band;
  return { zone: simplifyZone(clipKeep(Z, keep as Feature<Polygon>)), side: inCore ? 'core' : 'band' };
}

/** Photo-failure penalty (§4.3 / §5): the hider missed the upload window, so Z
 *  snaps to a disk that reveals the hider's ~radius neighbourhood — the only way
 *  a Photo card ever touches Z.
 *
 *  PRIVACY: the disk is NOT centred on `H` exactly. A disk centred on `H` would
 *  put H's coordinates (and a trivially-recoverable centroid) into the shared Z
 *  polygon — a leak of the exact hider point. Instead the centre is snapped to a
 *  coarse grid (cell = radius), so the seeker learns "the hider is in this ~r
 *  blob" without the exact location. The snap offset is ≤ r·√2/2 ≈ 0.707·r < r,
 *  so `H` is always strictly inside the disk (the true location is never
 *  excluded). The caller must also NEVER emit a radarPing for this (that field
 *  would carry the centre); only the resulting Z is shared. */
export function resolvePhotoPenalty(H: LngLat, Z: Poly, radiusM: number): { zone: Poly } {
  const cellLat = radiusM / 111_320;
  const cellLng = radiusM / (111_320 * Math.cos((H.lat * Math.PI) / 180));
  const center: LngLat = {
    lat: Math.round(H.lat / cellLat) * cellLat,
    lng: Math.round(H.lng / cellLng) * cellLng,
  };
  const disk = radarDisk(center, radiusM);
  return { zone: simplifyZone(clipKeep(Z, disk)) };
}

export type SliceOutcome = 'left' | 'right' | 'tie' | 'fail';
export interface SliceResult {
  zone: Poly;
  outcome: SliceOutcome;
}

/** Signed cross-track distance (metres) of H from the infinite A→B line.
 *  Sign convention: > 0 ⇒ H is to the RIGHT of the walk, < 0 ⇒ LEFT — matching
 *  sliceHalfPlane's `walk + 90` = right. Magnitude is the perpendicular offset,
 *  used for the tie band. */
function crossTrackM(A: LngLat, B: LngLat, H: LngLat): number {
  const R = 6371008.8; // mean Earth radius (m), turf's default sphere
  const d13 = distM(A, H) / R; // angular distance A→H
  const th13 = (turfBearing(pt(A), pt(H)) * Math.PI) / 180;
  const th12 = (turfBearing(pt(A), pt(B)) * Math.PI) / 180;
  return Math.asin(Math.sin(d13) * Math.sin(th13 - th12)) * R;
}

/**
 * Slice (v21 BOLT-101 — was Thermometer; wire family/id stay 'thermometer'):
 *  • displacement = dist(Pstart, Pend); if < min ⇒ FAIL, Z unchanged.
 *  • tie: H within ε metres of the A→B line ⇒ NO clip (never exclude H).
 *  • else clip Z to the half-plane on H's side of the line the seeker just
 *    walked. The line is the WALK itself (A→B), not a bisector of it.
 */
export function resolveSlice(
  Pstart: LngLat,
  Pend: LngLat,
  H: LngLat,
  Z: Poly,
  minM: number,
): SliceResult {
  if (distM(Pstart, Pend) < minM) return { zone: Z, outcome: 'fail' };
  if (Math.abs(crossTrackM(Pstart, Pend, H)) <= THERMO_TIE_EPSILON_M) return { zone: Z, outcome: 'tie' };
  // Keep the half that PROVABLY contains H (same builder the live preview draws,
  // §2G) — the point-in-polygon is the authority, so the wrong half can't be kept.
  const right = sliceHalfPlane(Pstart, Pend, 'R');
  const onRight = booleanPointInPolygon(pt(H), right);
  const half = onRight ? right : sliceHalfPlane(Pstart, Pend, 'L');
  return { zone: simplifyZone(clipKeep(Z, half)), outcome: onRight ? 'right' : 'left' };
}
